import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Copy, Loading, SystemText } from 'e4shared';

import { setShowSuccess } from 'actions/ui';
import Rating from 'components/prism/rating';
import PositiveIndicatorTable from 'components/prism/positiveIndicatorTable';
import { apiGet, apiPatch } from 'services';

import Bullet from '../bullet';
import Course from '../course';
import CourseCard from '../courseCard';
import Subheader from '../subheader';
import Metric from '../metric';

export const BehaviourCard = ({ profile, behaviour, opened, setOpened, setShowManageTask }) => {
    const dispatch = useDispatch();
    const [behaviourDetails, setBehaviourDetails] = React.useState({});
    const [loadingBehaviour, setLoadingBehaviour] = React.useState(true);

    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    React.useEffect(() => {
        if (profile?.id && opened) {
            setLoadingBehaviour(true);
            apiGet(`team-member/overview/${profile.id}/behaviour/${behaviour.id}`, user.guid, response => {
                setBehaviourDetails(response);
                setLoadingBehaviour(false);
            });
        }
    }, [opened, profile?.id, behaviour?.id]);

    const setFlagStatus = (id, flagged) => {
        const item = {
            id,
            flagged,
            profile_Id: profile.id,
        };

        apiPatch('flag', item, user.guid, () => {
            behaviour.flagged = flagged;
            dispatch(setShowSuccess(text.flagSuccess));
        });
    };

    const text = {
        showMore: SystemText('TeamMember/Overview/ShowMore'),
        showLess: SystemText('TeamMember/Overview/ShowLess'),
        buttons: {
            setObjective: SystemText('TeamMember/Overview/Buttons/Objective'),
            flag: SystemText('TeamMember/Overview/Buttons/Flag'),
            unflag: SystemText('TeamMember/Overview/Buttons/Unflag'),
        },
        courses: x =>
            x === 1 ? SystemText('Controls/Behavior/Courses', x) : SystemText('Controls/Behavior/CoursesPlural', x),
        metrics: x =>
            x === 1 ? SystemText('Controls/Behavior/Metrics', x) : SystemText('Controls/Behavior/MetricsPlural', x),
        flagSuccess: SystemText('TeamMember/Overview/FlagSuccess'),
        metricsRelated: SystemText('TeamMember/Overview/MetricsRelated'),
        howWeMeasure: SystemText('TeamMember/Overview/HowWeMeasure'),
        outstandingObjectives: SystemText('TeamMember/Overview/OutstandingObjectives'),
        coursesToHelp: SystemText('TeamMember/Overview/CoursesToHelp'),
    };

    return (
        <div key={`behaviour-${behaviour.id}`} className="prism-content-box faded mb10">
            <div className="prism-content">
                <div className="flex flex-center">
                    <h2 className="mr10">{behaviour.name}</h2>
                    <span className="flex-grow mr10">
                        <Rating level={behaviour.level} />
                    </span>
                    <button onClick={() => setOpened(behaviour.id)} className="btn btn-rounded btn-tertiary">
                        {opened ? text.showLess : text.showMore}
                    </button>
                </div>
                {behaviour.description && <Copy value={behaviour.description} />}
                <div>
                    <b>{text.courses(behaviour.courseCount)}</b> &bull; <b>{text.metrics(behaviour.metricCount)}</b>
                </div>
                <div>
                    {setShowManageTask && (
                        <button
                            className="btn btn-primary btn-rounded mr10"
                            onClick={e => {
                                e.stopPropagation();
                                setShowManageTask?.(behaviour.id);
                            }}
                        >
                            {text.buttons.setObjective}
                        </button>
                    )}
                    <button
                        className={`btn ${behaviour.flagged ? 'btn-tertiary' : 'btn-primary'} btn-rounded`}
                        onClick={e => {
                            e.stopPropagation();
                            setFlagStatus(behaviour.id, !behaviour.flagged);
                        }}
                    >
                        {behaviour.flagged ? text.buttons.unflag : text.buttons.flag}
                    </button>
                </div>
                {opened && (
                    <>
                        <hr className={`mt20 ${loadingBehaviour ? '' : 'mb20'}`} />
                        {loadingBehaviour ? (
                            <div>
                                <Loading />
                            </div>
                        ) : (
                            <>
                                {behaviourDetails.metrics.length > 0 && (
                                    <>
                                        <Subheader title={text.metricsRelated} />
                                        <div className="mt10">
                                            <div className={`${prismMultiColumn ? 'four-col' : 'two-col'}`}>
                                                {behaviourDetails.metrics.slice(0, 4).map((x, i) => {
                                                    return <Metric noShadow period={'M'} metric={x} />;
                                                })}
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div
                                    className={`${prismMultiColumn && behaviourDetails.objectives.length > 0 ? 'two-col-66' : ''}`}
                                >
                                    <div className="mt20">
                                        <Subheader title={text.howWeMeasure} />
                                        <div className="mt10">
                                            <PositiveIndicatorTable data={behaviourDetails.positiveIndicators} />
                                        </div>
                                    </div>
                                    {behaviourDetails.objectives.length > 0 && (
                                        <div className="mt20">
                                            <Subheader title={text.outstandingObjectives} />
                                            <div className="mt10 bullet-list">
                                                {behaviourDetails.objectives.map(x => {
                                                    return (
                                                        <Link
                                                            key={`obj-${x.id}`}
                                                            to={`/objectives/-1/objective/${x.profile_Task_Id}`}
                                                        >
                                                            <Bullet value={x.name} style="steel round" />
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {behaviourDetails.courses.length > 0 && (
                                    <div className="mt20">
                                        <Subheader title={text.coursesToHelp} />
                                        {behaviourDetails.courses.length === 1 ? (
                                            <CourseCard noShadow course={behaviourDetails.courses[0]} />
                                        ) : (
                                            <div className={prismMultiColumn ? 'even-col' : 'one-col'}>
                                                {behaviourDetails.courses.map(course => (
                                                    <Course
                                                        noShadow
                                                        key={`course-${course.id}`}
                                                        course={course}
                                                        padBottom={!prismMultiColumn}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BehaviourCard;
